import * as React from 'react';

import {
  createContext,
  InputProps,
  useField,
  useForm,
  useNotification,
} from '@strapi/admin/strapi-admin';
import { Box, Flex, VisuallyHidden } from '@strapi/design-system';
import pipe from 'lodash/fp/pipe';
import { useIntl } from 'react-intl';

import { useDocumentContext } from '../../../../../hooks/useDocumentContext';
import { type EditFieldLayout } from '../../../../../hooks/useDocumentLayout';
import { usePrev } from '../../../../../hooks/usePrev';
import { getTranslation } from '../../../../../utils/translations';
import { transformDocument } from '../../../utils/data';
import { createDefaultForm } from '../../../utils/forms';
import { ComponentProvider, useComponent } from '../ComponentContext';

import { AddComponentButton } from './AddComponentButton';
import { ComponentPicker } from './ComponentPicker';
import { DynamicComponent, DynamicComponentProps } from './DynamicComponent';
import { DynamicZoneLabel, DynamicZoneLabelProps } from './DynamicZoneLabel';

import type { InputRendererProps } from '../../InputRenderer';
import type { Schema } from '@strapi/types';

interface DynamicZoneContextValue {
  isInDynamicZone: boolean;
}

const [DynamicZoneProvider, useDynamicZone] = createContext<DynamicZoneContextValue>(
  'DynamicZone',
  {
    isInDynamicZone: false,
  }
);

interface DynamicZoneProps
  extends Omit<Extract<EditFieldLayout, { type: 'dynamiczone' }>, 'size' | 'hint'>,
    Pick<InputProps, 'hint'>,
    Pick<DynamicZoneLabelProps, 'labelAction'> {
  children?: (props: InputRendererProps) => React.ReactNode;
}

const DynamicZone = ({
  attribute,
  disabled: disabledProp,
  hint,
  label,
  labelAction,
  name,
  required = false,
  children,
}: DynamicZoneProps) => {
  // We cannot use the default props here
  const { max = Infinity, min = -Infinity } = attribute ?? {};

  const [addComponentIsOpen, setAddComponentIsOpen] = React.useState(false);
  const [liveText, setLiveText] = React.useState('');
  const [openComponentKey, setOpenComponentKey] = React.useState<string | null>(null);

  const {
    currentDocument: { components, isLoading },
  } = useDocumentContext('DynamicZone');

  const disabled = disabledProp || isLoading;
  const addFieldRow = useForm('DynamicZone', (state) => state.addFieldRow);
  const removeFieldRow = useForm('DynamicZone', (state) => state.removeFieldRow);
  const moveFieldRow = useForm('DynamicZone', (state) => state.moveFieldRow);

  type DzWithTempKey =
    Schema.Attribute.GetDynamicZoneValue<Schema.Attribute.DynamicZone>[number] & {
      __temp_key__: string;
    };

  const { value = [], error } = useField<Array<DzWithTempKey>>(name);

  /**
   * Track the previous value array to detect when a new component is added.
   * When the array grows, we find the newly added item and force its accordion open.
   * This mirrors the same pattern used in RepeatableComponent.
   */
  const prevValue = usePrev(value);

  React.useEffect(() => {
    if (prevValue && prevValue.length < value.length) {
      const prevKeys = new Set(prevValue.map((v) => v.__temp_key__));
      const newItem = value.find((v) => !prevKeys.has(v.__temp_key__));
      if (newItem) {
        setOpenComponentKey(newItem.__temp_key__);
      }
    } else if (openComponentKey !== null) {
      // Component was removed before forceOpen was handled — clear stale key
      const currentKeys = new Set(value.map((v) => v.__temp_key__));
      if (!currentKeys.has(openComponentKey)) {
        setOpenComponentKey(null);
      }
    }
  }, [value, prevValue, openComponentKey]);

  const handleForceOpenHandled = React.useCallback(() => {
    setOpenComponentKey(null);
  }, []);

  const dynamicComponentsByCategory = React.useMemo(() => {
    return attribute.components.reduce<
      NonNullable<DynamicComponentProps['dynamicComponentsByCategory']>
    >((acc, componentUid) => {
      const componentSchema = components[componentUid];

      if (!componentSchema) {
        return acc;
      }

      const { category, info } = componentSchema;

      const component = { uid: componentUid, displayName: info.displayName, icon: info.icon };

      if (!acc[category]) {
        acc[category] = [];
      }

      acc[category] = [...acc[category], component];

      return acc;
    }, {});
  }, [attribute.components, components]);

  const { formatMessage } = useIntl();

  const { toggleNotification } = useNotification();

  const dynamicDisplayedComponentsLength = value.length;

  const handleAddComponent = React.useCallback(
    (uid: string, position?: number) => {
      const schema = components[uid];

      if (!schema) {
        return;
      }

      setAddComponentIsOpen(false);

      const form = createDefaultForm(schema, components);
      const transformations = pipe(transformDocument(schema, components), (data) => ({
        ...data,
        __component: uid,
      }));

      const data = transformations(form);

      addFieldRow(name, data, position);
    },
    [addFieldRow, components, name]
  );

  const handleClickOpenPicker = () => {
    if (dynamicDisplayedComponentsLength < max) {
      setAddComponentIsOpen((prev) => !prev);
    } else {
      toggleNotification({
        type: 'info',
        message: formatMessage({
          id: getTranslation('components.notification.info.maximum-requirement'),
        }),
      });
    }
  };

  const handleMoveComponent = React.useCallback(
    (newIndex: number, currentIndex: number) => {
      setLiveText(
        formatMessage(
          {
            id: getTranslation('dnd.reorder'),
            defaultMessage: '{item}, moved. New position in list: {position}.',
          },
          {
            item: `${name}.${currentIndex}`,
            position: `${newIndex + 1} of ${value.length}`,
          }
        )
      );

      moveFieldRow(name, currentIndex, newIndex);
    },
    [formatMessage, moveFieldRow, name, value.length]
  );

  const handleCancel = React.useCallback(
    (index: number) => {
      setLiveText(
        formatMessage(
          {
            id: getTranslation('dnd.cancel-item'),
            defaultMessage: '{item}, dropped. Re-order cancelled.',
          },
          {
            item: `${name}.${index}`,
          }
        )
      );
    },
    [formatMessage, name]
  );

  const handleGrabItem = React.useCallback(
    (index: number) => {
      setLiveText(
        formatMessage(
          {
            id: getTranslation('dnd.grab-item'),
            defaultMessage: `{item}, grabbed. Current position in list: {position}. Press up and down arrow to change position, Spacebar to drop, Escape to cancel.`,
          },
          {
            item: `${name}.${index}`,
            position: `${index + 1} of ${value.length}`,
          }
        )
      );
    },
    [formatMessage, name, value.length]
  );

  const handleDropItem = React.useCallback(
    (index: number) => {
      setLiveText(
        formatMessage(
          {
            id: getTranslation('dnd.drop-item'),
            defaultMessage: `{item}, dropped. Final position in list: {position}.`,
          },
          {
            item: `${name}.${index}`,
            position: `${index + 1} of ${value.length}`,
          }
        )
      );
    },
    [formatMessage, name, value.length]
  );

  const handleRemoveComponent = React.useCallback(
    (currentIndex: number) => {
      removeFieldRow(name, currentIndex);
    },
    [name, removeFieldRow]
  );

  const hasError = error !== undefined;

  const renderButtonLabel = () => {
    if (addComponentIsOpen) {
      return formatMessage({ id: 'app.utils.close-label', defaultMessage: 'Close' });
    }

    if (hasError && dynamicDisplayedComponentsLength > max) {
      return formatMessage(
        {
          id: getTranslation(`components.DynamicZone.extra-components`),
          defaultMessage:
            'There {number, plural, =0 {are # extra components} one {is # extra component} other {are # extra components}}',
        },
        {
          number: dynamicDisplayedComponentsLength - max,
        }
      );
    }

    if (hasError && dynamicDisplayedComponentsLength < min) {
      return formatMessage(
        {
          id: getTranslation(`components.DynamicZone.missing-components`),
          defaultMessage:
            'There {number, plural, =0 {are # missing components} one {is # missing component} other {are # missing components}}',
        },
        { number: min - dynamicDisplayedComponentsLength }
      );
    }

    return formatMessage(
      {
        id: getTranslation('components.DynamicZone.add-component'),
        defaultMessage: 'Add a component to {componentName}',
      },
      { componentName: label || name }
    );
  };

  const level = useComponent('DynamicZone', (state) => state.level);

  const ariaDescriptionId = React.useId();

  return (
    <DynamicZoneProvider isInDynamicZone>
      <Flex direction="column" alignItems="stretch" gap={{ initial: 4, medium: 6 }}>
        {dynamicDisplayedComponentsLength > 0 && (
          <Box>
            <DynamicZoneLabel
              hint={hint}
              label={label}
              labelAction={labelAction}
              name={name}
              numberOfComponents={dynamicDisplayedComponentsLength}
              required={required}
            />
            <VisuallyHidden id={ariaDescriptionId}>
              {formatMessage({
                id: getTranslation('dnd.instructions'),
                defaultMessage: `Press spacebar to grab and re-order`,
              })}
            </VisuallyHidden>
            <VisuallyHidden aria-live="assertive">{liveText}</VisuallyHidden>
            <ol aria-describedby={ariaDescriptionId}>
              {value.map((field, index) => (
                <ComponentProvider
                  key={field.__temp_key__}
                  level={level + 1}
                  uid={field.__component}
                  // id is always a number in a dynamic zone.
                  id={field.id as number}
                  type="dynamiczone"
                >
                  <DynamicComponent
                    disabled={disabled}
                    name={name}
                    index={index}
                    componentUid={field.__component}
                    onMoveComponent={handleMoveComponent}
                    onRemoveComponentClick={handleRemoveComponent}
                    onCancel={handleCancel}
                    onDropItem={handleDropItem}
                    onGrabItem={handleGrabItem}
                    onAddComponent={handleAddComponent}
                    dynamicComponentsByCategory={dynamicComponentsByCategory}
                    totalLength={dynamicDisplayedComponentsLength}
                    forceOpen={openComponentKey === field.__temp_key__}
                    onForceOpenHandled={handleForceOpenHandled}
                  >
                    {children}
                  </DynamicComponent>
                </ComponentProvider>
              ))}
            </ol>
          </Box>
        )}
        <Flex justifyContent="center">
          <AddComponentButton
            hasError={hasError}
            isDisabled={disabled}
            isOpen={addComponentIsOpen}
            onClick={handleClickOpenPicker}
          >
            {renderButtonLabel()}
          </AddComponentButton>
        </Flex>
        <ComponentPicker
          dynamicComponentsByCategory={dynamicComponentsByCategory}
          isOpen={addComponentIsOpen}
          onClickAddComponent={handleAddComponent}
        />
      </Flex>
    </DynamicZoneProvider>
  );
};

export { DynamicZone, useDynamicZone };
export type { DynamicZoneProps };
